class CartShare extends HTMLElement {
  constructor() {
    super();

    this.shareButton = this.querySelector('.cart__share button');
    this.modal = this.querySelector('.cart__share--modal');
    this.closeButton = this.querySelector('.cart__share--modal-header button');
    this.copyButton = this.querySelector('.cart__share--modal-wrapper button');
    this.urlInput = this.querySelector('#share_url');
    this.successNotice = this.querySelector('.cart__share--modal-notice');

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.copyToClipboard = this.copyToClipboard.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.generateShareLink = this.generateShareLink.bind(this);
    this.checkForSharedCart = this.checkForSharedCart.bind(this);
    this.loadSharedCart = this.loadSharedCart.bind(this);
  }

  loadingOverlay() {
    return document.querySelector('.loading-overlay');
  }

  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    if (!this.shareButton || !this.modal) return;

    this.checkForSharedCart();

    this.shareButton.addEventListener('click', this.openModal, { signal });
    this.closeButton.addEventListener('click', this.closeModal, { signal });
    this.copyButton.addEventListener('click', this.copyToClipboard, { signal });
    this.modal.addEventListener('click', this.handleOutsideClick, { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  handleOutsideClick(event) {
    if (event.target === this.modal) {
      this.closeModal(event);
    }
  }

  openModal(event) {
    event.preventDefault();
    this.modal.style.display = 'block';

    this.generateShareLink();
  }

  closeModal(event) {
    if (event) event.preventDefault();

    this.modal.style.display = 'none';
    this.successNotice.style.display = 'none';
  }

  copyToClipboard(event) {
    event.preventDefault();

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(this.urlInput.value)
        .then(() => this.showSuccessMessage())
        .catch(() => this.legacyCopyToClipboard());
    } else {
      this.legacyCopyToClipboard();
    }
  }

  legacyCopyToClipboard() {
    this.urlInput.select();
    document.execCommand('copy');
    this.showSuccessMessage();
  }

  showSuccessMessage() {
    this.successNotice.style.display = 'block';

    setTimeout(() => {
      this.successNotice.style.display = 'none';
    }, 3000);
  }

  async generateShareLink() {
    try {
      const response = await fetch('/cart.js', { signal: this.abortController.signal });
      const cart = await response.json();

      if (!cart.items || cart.items.length === 0) {
        return;
      }

      const cartData = {
        items: cart.items.map((item) => ({
          id: item.variant_id,
          quantity: item.quantity,
        })),
      };

      const compressedData = btoa(JSON.stringify(cartData));

      const shareUrl = new URL(window.location.origin + window.location.pathname);

      shareUrl.searchParams.set('shared_cart', compressedData);

      this.urlInput.value = shareUrl.toString();
    } catch (error) {
      if (error.name !== 'AbortError') {
        throw new Error(`Error generating share link: ${error.message || error}`);
      }
    }
  }

  checkForSharedCart() {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedCartParam = urlParams.get('shared_cart');

    if (sharedCartParam) {
      this.loadSharedCart(sharedCartParam);

      return true;
    }

    return false;
  }

  async loadSharedCart(sharedCartData) {
    try {
      const loadingOverlay = this.loadingOverlay();

      if (loadingOverlay) {
        loadingOverlay.classList.remove('hidden');

        const cartContent = document.querySelector('.cart__contents');
        const cartWarnings = document.querySelector('.cart__warnings');

        if (cartContent) cartContent.style.opacity = '0.3';

        if (cartWarnings) cartWarnings.style.display = 'none';
      }

      let cartData;

      try {
        cartData = JSON.parse(atob(sharedCartData));
      } catch {
        throw new Error('Invalid shared cart data');
      }

      if (!cartData.items || !Array.isArray(cartData.items)) {
        throw new Error('Invalid cart format');
      }

      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: this.abortController.signal,
      });

      const updates = {
        items: cartData.items,
      };

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
        signal: this.abortController.signal,
      });

      const url = new URL(window.location.href);

      url.searchParams.delete('shared_cart');
      window.location.href = url.toString();
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('Error loading shared cart:', error);

      const loadingOverlay = this.loadingOverlay();

      if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
      }

      const url = new URL(window.location.href);

      url.searchParams.delete('shared_cart');
      window.history.replaceState({}, document.title, url.toString());
    }
  }
}

customElements.define('cart-share', CartShare);
